<template>
  <el-dialog
    :title="title"
    :visible="visible"
    :append-to-body="true"
    :width="width"
    :lock-scroll="true"
    :class="{'ide-dialog': true, ...dialogClass}"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :fullscreen="fullscreen"
    :show-close="withCloseIcon"
    @close="close"
  >
    <div slot="title" class="dialog-title">
      <slot name="title" :title="title" :close="close">{{ title }}</slot>
    </div>
    <div class="dialog-wrap">
      <slot></slot>
    </div>
    <div class="dialog-footer" v-if="withFooter">
      <div class="opera-box">
        <div class="footer-left">
          <slot name="footer-left">
            <div class="error-message">
              <template v-if="errorMessage != null && errorMessage != ''">{{this.errorMessage}}</template>
            </div>
          </slot>
        </div>
        <div class="footer-right">
          <el-button
            v-if="withClose"
            class="btn-cancel"
            type="text"
            round
            :disabled="confirmWorking"
            @click="close"
          >
            {{closeText}}
          </el-button>
          <el-button
            v-if="withConfirm"
            round
            class="btn-confirm"
            type="primary"
            :loading="confirmWorking"
            :disabled="confirmWorking"
            @click="confirm"
          >
            {{confirmText}}
          </el-button>
        </div>
      </div>
      <slot name="footer-extends"></slot>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'IDEDialog',
  props: {
    // 标题
    title: {
      type: String,
      require: true
    },
    // 是否显示
    visible: {
      type: Boolean
    },
    // 宽度
    width: {
      default: '50%'
    },
    // class
    dialogClass: {
      type: Object,
      default () {
        return {}
      }
    },
    // 是否全屏
    fullscreen: {
      type: Boolean,
      default: false
    },
    // 是否需要底部操作部分
    withFooter: {
      type: Boolean,
      default: true
    },
    // 是否需要关闭按钮
    withClose: {
      type: Boolean,
      default: true
    },
    // 是否需要关闭图标
    withCloseIcon: {
      type: Boolean,
      default: true
    },
    // 关闭按钮文案
    closeText: {
      type: String,
      default: '关闭'
    },
    // 是否需要确认按钮
    withConfirm: {
      type: Boolean,
      default: true
    },
    // 确认按钮文案
    confirmText: {
      type: String,
      default: '确认'
    },
    // 确认按钮是否正在工作中
    confirmWorking: {
      type: Boolean,
      default: false
    },
    // 确认按钮工作中文案
    confirmWorkingText: {
      type: String,
      require: false
    },
    // 关闭时是否需要确认
    withCloseConfirm: {
      type: Boolean,
      default: false
    },
    // 关闭确认函数（返回true时表示需要关闭确认）
    closeConfirmValidator: {
      type: Function,
      default () {
        return () => true
      }
    },
    // 关闭时确认文案
    closeConfirmText: {
      default: '确认关闭吗？'
    },
    // 关闭确认选项
    closeConfirmOption: {
      default () {
        return {
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        }
      }
    },
    // 关闭被确认执行函数
    closeConfirmFunction: {
      type: Function,
      default () {
        return Promise.resolve()
      }
    },
    // 关闭被取消执行函数
    closeCancelFunction: {
      type: Function,
      default () {
        return Promise.resolve()
      }
    },
    // 是否可通过点击背景面板关闭
    closeOnClickModal: {
      type: Boolean,
      default: false
    },
    // 是否可通过按下ESC键关闭
    closeOnPressEscape: {
      type: Boolean,
      default: false
    },
    // 错误消息
    errorMessage: {
      type: String
    }
  },
  data () {
    return {
      // 已确认关闭操作，防止出现两次关闭确认弹窗
      confirmClose: false
    }
  },
  watch: {
    // 显示时，重置确认关闭操作
    visible () {
      if (this.visible) {
        this.confirmClose = false
      }
    }
  },
  methods: {
    // 关闭
    close () {
      // 如果正在处理，使点击取消时不生效
      if (this.confirmWorking) {
        return
      }
      // 如果存在取消确认，则弹出确认框
      if (this.withCloseConfirm && !this.confirmClose && this.closeConfirmValidator()) {
        this.confirmClose = true
        this.$confirm(this.closeConfirmText, {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning',
          roundButton: true,
          closeOnClickModal: false,
          ...this.closeConfirmOption
        })
          .then(() => {
            this.closeConfirmFunction()
              .then(() => {
                this.$emit('close')
              })
          })
          .catch(() => {
            this.closeCancelFunction()
              .then(() => {
                this.$emit('close')
              })
          })
        return
      }
      this.$emit('close')
    },
    // 确认
    confirm () {
      if (this.confirmWorking) {
        return
      }
      this.$emit('confirm')
    }
  }
}
</script>

<style scoped lang="scss">
@import "./variables";
.ide-dialog {
  // 头部
  /deep/ .el-dialog__header {
    background: $menu-background;
    padding: 10px 20px;
    // 标题
    .dialog-title {
      color: $color-white;
      font-size: 14px;
      /deep/ .icon {
        background-size: 16px;
        vertical-align: middle;
        margin-right: 3px;
      }
    }
    // 关闭按钮
    .el-dialog__headerbtn {
      top: 10px;
      .el-dialog__close {
        font-size: 18px;
      }
    }
  }
  // 主体部分
  /deep/ .el-dialog__body {
    background: $menu-background;
    padding: 0;
    font-size: 12px;
    color: $color-white;
    .dialog-wrap {
      padding: 0 20px;
    }
  }
  // 操作部分
  .dialog-footer {
    box-shadow: none;
    width: 100%;
    padding: 12px 20px;
    box-sizing: border-box;
    .opera-box {
      display: flex;
      .footer-left {
        width: 100%;
        line-height: 30px;
        // 错误消息
        .error-message {
          color: #F74242;
        }
      }
      .footer-right {
        width: 180px;
        flex-shrink: 0;
        text-align: right;
        button {
          border: 0;
          height: 28px;
          padding: 0 24px;
          background: transparent !important;
        }
        .btn-cancel {
          color: $color-gray;
        }
        .btn-confirm {
          background: $primary-color !important;
          border: 1px solid $primary-color - 10;
          &.is-disabled {
            background: #999 !important;
          }
        }
        button:disabled {
          &:hover {
            color: unset !important;
          }
        }
      }
    }
  }
}

</style>
